import { Formik } from "formik";

import { EmailVerificationSettingsCard } from "@src/features/auth/v2";
import { FormikInput } from "@src/shared/components/forms";
import { HeaderText } from "@src/shared/components/general/HeaderText";
import { Text } from "@src/shared/components/general/Text";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { Button } from "@src/shared/components/ui/button";
import { Spinner } from "@src/shared/components/ui/spinner";

import {
  StaffProfileFormValidationSchema,
  type StaffProfileFormValues,
} from "../../../../domain";

export interface StaffAccountSettingsPageV2Props {
  email: string;
  isEmailVerified: boolean;
  initialValues: StaffProfileFormValues;
  isSendingVerificationEmail?: boolean;
  isSaving?: boolean;
  verificationEmailError?: string | null;
  verificationEmailSent?: boolean;
  onSendVerificationEmail: () => void | Promise<void>;
  saveError?: string | null;
  onSubmit: (values: StaffProfileFormValues) => void | Promise<void>;
}

export function StaffAccountSettingsPageV2({
  email,
  isEmailVerified,
  initialValues,
  isSendingVerificationEmail = false,
  isSaving = false,
  verificationEmailError = null,
  verificationEmailSent = false,
  onSendVerificationEmail,
  saveError = null,
  onSubmit,
}: StaffAccountSettingsPageV2Props) {
  return (
    <VerticalFlexbox
      className="mx-auto w-full max-w-[720px] px-4 py-6 md-stitch:px-8 md-stitch:py-8"
      gap="none"
    >
      <HeaderText tag="h2" className="mb-8 text-[22px] tracking-tight">
        Account
      </HeaderText>

      <EmailVerificationSettingsCard
        email={email}
        isSendingVerificationEmail={isSendingVerificationEmail}
        isVerified={isEmailVerified}
        onSendVerificationEmail={onSendVerificationEmail}
        verificationEmailError={verificationEmailError}
        verificationEmailSent={verificationEmailSent}
      />

      <Formik<StaffProfileFormValues>
        enableReinitialize
        initialValues={initialValues}
        validationSchema={StaffProfileFormValidationSchema}
        onSubmit={async (values) => {
          await onSubmit(values);
        }}
      >
        {({ dirty, handleSubmit, isSubmitting }) => {
          const submitting = isSaving || isSubmitting;

          return (
            <VerticalFlexbox as="form" gap="none" onSubmit={handleSubmit}>
              <VerticalFlexbox
                aria-labelledby="staff-basic-info-heading"
                as="section"
                gap="md"
              >
                <Text
                  as="h3"
                  fontWeight={600}
                  id="staff-basic-info-heading"
                  sizing="sm"
                >
                  Basic information
                </Text>

                <HorizontalFlexbox
                  className="flex-col sm-stitch:flex-row"
                  gap="md"
                >
                  <FormikInput
                    autoComplete="given-name"
                    containerClassName="min-w-0 flex-1"
                    disabled={submitting}
                    label="First name"
                    name="firstName"
                    required
                  />
                  <FormikInput
                    autoComplete="family-name"
                    containerClassName="min-w-0 flex-1"
                    disabled={submitting}
                    label="Last name"
                    name="lastName"
                    required
                  />
                </HorizontalFlexbox>

                {saveError ? (
                  <Text appearance="danger" role="alert" sizing="sm">
                    {saveError}
                  </Text>
                ) : null}

                <Button
                  className="w-full sm-stitch:w-auto"
                  disabled={!dirty || submitting}
                  type="submit"
                >
                  {submitting ? <Spinner className="mr-1" size="sm" /> : null}
                  {submitting ? "Saving..." : "Update information"}
                </Button>
              </VerticalFlexbox>
            </VerticalFlexbox>
          );
        }}
      </Formik>
    </VerticalFlexbox>
  );
}
